import { create } from 'zustand';
import { saveSession, clearSession } from '@core/auth/session';
import { requestContext } from '@core/api/requestContext';
import { useContextStore } from '@app/store/context.store';
import { authService } from '@/services';
import type { Tenant } from '@/services/tenant.service';
import type { AuthUser } from '@features/auth/types';
import { normalizeAuthUser } from '@features/auth/types';

function tenantFromMembership(m: {
  tenantId: string;
  tenantName: string;
}): Tenant {
  return {
    id: m.tenantId,
    name: m.tenantName,
    slug: '',
    status: 'active',
  };
}

interface AuthStore {
  user: AuthUser | null;
  token: string | null;
  tenantId: string | null;
  isAuthenticated: boolean;
  bootstrapped: boolean;

  login: (user: AuthUser, token: string, tenantId: string | null) => void;
  logout: () => void;
  setUser: (user: AuthUser) => void;
  setActiveTenant: (tenantId: string | null) => void;
  switchActiveTenant: (tenantId: string) => Promise<AuthUser>;
  applyActAsTenant: (tenant: Tenant) => Promise<AuthUser>;
  setBootstrapped: (value: boolean) => void;
}

export const useAuthStore = create<AuthStore>()((set, get) => ({
  user: null,
  token: null,
  tenantId: null,
  isAuthenticated: false,
  bootstrapped: false,

  login: (user, token, tenantId) => {
    const normalized = normalizeAuthUser(user);
    const tid =
      (tenantId && tenantId.length ? tenantId : null) ??
      normalized.activeContext.tenantId ??
      normalized.tenantId;
    saveSession({ token, tenantId: tid ?? '', userId: normalized.id });
    requestContext.setTenantId(tid);
    if (tid) {
      useContextStore.getState().setActiveTenantId(tid);
      const membership = normalized.memberships.find((m) => m.tenantId === tid);
      if (membership) {
        useContextStore
          .getState()
          .restoreActiveTenant(tenantFromMembership(membership));
      }
    }
    set({
      user: { ...normalized, tenantId: tid },
      token,
      tenantId: tid,
      isAuthenticated: true,
    });
  },

  logout: () => {
    clearSession();
    requestContext.clear();
    useContextStore.getState().clearAll();
    set({ user: null, token: null, tenantId: null, isAuthenticated: false });
  },

  setUser: (user) => set({ user: normalizeAuthUser(user) }),

  setActiveTenant: (tenantId) => {
    const tid = tenantId && tenantId.length ? tenantId : null;
    if (tid) requestContext.switchTenant(tid);
    else requestContext.setTenantId(null);
    set({ tenantId: tid });
    const user = get().user;
    if (user) {
      saveSession({
        token: get().token ?? '',
        tenantId: tid ?? '',
        userId: user.id,
      });
      set({
        user: {
          ...user,
          tenantId: tid,
          activeContext: tid
            ? { type: 'tenant', tenantId: tid }
            : { type: 'platform', tenantId: null },
        },
      });
    }
  },

  switchActiveTenant: async (tenantId) => {
    const session = await authService.setActiveTenant(tenantId);
    const token = get().token ?? '';
    useContextStore.getState().clearActiveAgent();
    requestContext.switchTenant(tenantId);

    const membership = session.memberships.find((m) => m.tenantId === tenantId);
    if (membership) {
      useContextStore
        .getState()
        .setActiveTenant(tenantFromMembership(membership));
    } else {
      useContextStore.getState().setActiveTenantId(tenantId);
    }

    saveSession({ token, tenantId, userId: session.id });
    set({
      user: { ...session, tenantId },
      tenantId,
      isAuthenticated: true,
    });
    return session;
  },

  applyActAsTenant: async (tenant) => {
    const session = await authService.setActiveTenant(tenant.id);
    const token = get().token ?? '';
    useContextStore.getState().setActiveTenant(tenant);
    saveSession({ token, tenantId: tenant.id, userId: session.id });
    set({
      user: { ...session, tenantId: tenant.id },
      tenantId: tenant.id,
      isAuthenticated: true,
    });
    return session;
  },

  setBootstrapped: (value) => set({ bootstrapped: value }),
}));
